import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useParams } from "react-router-dom";

import { DnsConfig, Domain } from "~/api/Api";
import type { ZoneImportErrors } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import NoData from "~/components/containers/NoData";
import Button from "~/components/mantine/button/Button";
import { useManualPoller } from "~/components/siteSpeed/hooks";
import { domainKeys } from "~/components/siteSpeed/queryKeys";
import ButtonWithSpinner from "~/components/ui/button/Button";
import Modal from "~/components/ui/modal/Modal";
import Spinner from "~/components/ui/spinner/Spinner";
import { useWizard } from "~/contexts/WizardContext";
import { pluralize } from "~/helpers/helpers";

import ActionsSiteSpeedDomain from "../ActionsSiteSpeedDomain";
import OriginsStatusModal from "../OriginsStatusModal";
import styles from "../SiteSpeedTable.module.scss";
import {
  POLLING_INTERVAL,
  apiDataToModels,
  buildPendingPayload,
  dnsPayloadModel,
  isAdd,
  isDelete,
  isDomainPrerenderingStatusHealthy,
  isUpdate,
  makeAddInitialData,
} from "../helpers";
import type {
  ChangesSummary,
  DnsPayloadRow,
  DnsRecordData,
  DnsRecordModel,
  MutationResult,
  SiteSpeedDomainTableProps,
} from "../types";
import EditRecordModal from "./EditRecordModal";
import { ImportZoneErrors } from "./ImportZoneErrors";
import RecordsTable from "./RecordsTable";
import { buildImportedChanges } from "./importUtils";

// we check if a record was manually added and then updated, so we keep "add" instead of "update"
function applyUpdatesToMap(
  prev: Map<number, DnsRecordModel>,
  updated: Map<number, DnsRecordModel>,
) {
  const next = new Map(prev);

  updated.forEach((record, id) => {
    const prevItem = next.get(id);
    const keepAdd = prevItem?.action === "add" && record.action === "update";
    next.set(id, keepAdd ? { ...record, action: "add" as const } : record);
  });

  return next;
}

export const UserConfigTable: React.FC<SiteSpeedDomainTableProps> = ({
  domain,
  isWizard,
  onErrorChange,
}) => {
  const wizard = useWizard();
  const queryClient = useQueryClient();
  const { domainId } = useParams<{ domainId: string }>();

  const [data, setData] = useState<DnsConfig>(domain?.user_config || []);
  const [allChangedRecords, setAllChangedRecords] = useState<
    Map<number, DnsRecordModel>
  >(new Map());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isOriginsModalOpen, setIsOriginsModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const lastErrorRef = React.useRef<string | null>(null);
  const [isOptimisticUpdatePending, setIsOptimisticUpdatePending] =
    useState(false);
  const [importZoneErrors, setImportZoneErrors] = useState<ZoneImportErrors>(
    [],
  );

  const queryKey = domainKeys.detail(domainId!);

  const updateLocalDomainCache = (nextDomain: Domain) => {
    queryClient.setQueryData<Domain>(queryKey, (prev) => {
      const keepPrevUserConfig = prev?.user_config && !nextDomain.user_config;

      return {
        ...(prev ?? {}),
        ...nextDomain,
        user_config: keepPrevUserConfig
          ? prev!.user_config
          : nextDomain.user_config,
      } as Domain;
    });
  };

  const updateData = (nextDomain: Domain) => {
    const baseConfig = nextDomain?.user_config_pending?.length
      ? nextDomain?.user_config_pending || []
      : nextDomain?.user_config || [];
    setData(baseConfig);
  };

  const handleBack = (): void => {
    if (!wizard) return;

    const prevStep = wizard.getPrevStep();

    if (!prevStep) return;

    wizard.navigateToPrevStep();
  };

  useEffect(() => {
    lastErrorRef.current = lastError;
  }, [lastError]);

  // ---- checkStatus mutation ----
  const { mutateAsync: checkStatus } = useMutation({
    mutationFn: async () => {
      const result = await apiClient.domains.statusDetail(Number(domainId));

      return result?.data?.domain;
    },
    onSuccess: (nextDomain) => {
      // reset all changes after 1st successful status check response
      if (allChangedRecords.size !== 0) {
        setAllChangedRecords(new Map());
      }
      // always update local cache
      updateLocalDomainCache(nextDomain);

      const errorMsg = nextDomain.user_config_error?.message;
      if (errorMsg && errorMsg !== lastErrorRef.current) {
        enqueueSnackbar(errorMsg, {
          variant: "error",
          persist: true,
        });
        setLastError(errorMsg);
      }

      updateData(nextDomain);

      const isTerminalDnsState = ["ready", "failed"].includes(
        nextDomain?.dns_state ?? "",
      );

      if (isTerminalDnsState && isPolling) {
        stopPolling();
        // clear lastError if any
        setLastError(null);

        // finally, it's not pending, refresh data and show results
        queryClient.invalidateQueries({ queryKey });

        if (nextDomain?.dns_state === "ready") {
          setIsOriginsModalOpen(true);
        } else if (nextDomain?.dns_state === "failed") {
          enqueueSnackbar(
            "Failed to apply DNS changes. Please review your settings.",
            { variant: "error", persist: true },
          );
        }
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to check verification status", {
        variant: "error",
        persist: true,
      });
      setAllChangedRecords(new Map());
      stopPolling();
    },
  });

  // start polling checkStatus() after successful domain update response
  // end polling checkStatus() if domain dns state is terminal
  const {
    start: startPolling,
    stop: stopPolling,
    isPolling,
  } = useManualPoller(async () => {
    await checkStatus();
  }, POLLING_INTERVAL);

  // ---- update mutation ----
  const { mutate: updateDnsRecord, isPending: isUpdateDnsPending } =
    useMutation<MutationResult, Error, Map<number, DnsRecordModel>>({
      onMutate: () => setIsOptimisticUpdatePending(true),
      onSettled: () => setIsOptimisticUpdatePending(false),
      mutationFn: async (changes) => {
        // using buildPendingPayload which combines server data + local changes
        const baseModels = apiDataToModels(data || [], domain?.name || "");
        const payload = buildPendingPayload(baseModels, changes);
        await apiClient.domains.domainsPartialUpdate(Number(domainId), {
          user_config_pending: payload,
        });
        const arr = Array.from(changes.values());

        return {
          adds: arr.filter(isAdd),
          updates: arr.filter(isUpdate),
          deletes: arr.filter(isDelete),
        };
      },
      onSuccess: async ({ adds = [], updates = [], deletes = [] }) => {
        const parts: string[] = [];
        if (adds.length) parts.push(`${adds.length} added`);
        if (updates.length) parts.push(`${updates.length} updated`);
        if (deletes.length) parts.push(`${deletes.length} deleted`);
        if (parts.length) {
          enqueueSnackbar(
            `DNS records updated successfully. ${parts.join(", ")}.`,
            { variant: "success" },
          );
        }
        startPolling();
      },
      // @ts-expect-error BE types
      onError: async ({ error }) => {
        const errorMessage =
          error?.error?.message || "Error updating DNS records";
        enqueueSnackbar(`Error updating DNS records: ${errorMessage}`, {
          variant: "error",
          persist: true,
          // @ts-expect-error TS2339 non-existing className
          className: styles.wideErrorSnackbar,
        });
        setAllChangedRecords(new Map());
        await queryClient.refetchQueries({ queryKey });
        const nextDomain = queryClient.getQueryData<Domain>(queryKey);
        if (nextDomain) updateData(nextDomain);
      },
    });

  const handleEditFormSubmit = useCallback(
    (updatedRecordMap: Map<number, DnsRecordModel>) => {
      setAllChangedRecords((prev) => applyUpdatesToMap(prev, updatedRecordMap));
    },
    [],
  );

  const handleOriginsConfirm = async () => {
    wizard?.navigateToNextStep();
  };

  // id is a seq number, so fetch DnsPayloadRow from data and normalize for DnsRecordModel
  const handleDelete = (id: number) => {
    setAllChangedRecords((prev) => {
      const next = new Map(prev); // copy state
      const prevRecord = next.get(id);
      // If record was added locally - just remove it (don't need to do it on server)
      if (prevRecord?.action === "add") {
        next.delete(id);

        return next;
      }
      // otherwise, create a delete-action record
      const [name, ttl, type, content] = (data?.[id] ?? []) as DnsPayloadRow;
      const deleted: DnsRecordModel = dnsPayloadModel({
        id,
        name,
        ttl,
        type,
        content,
        action: "delete",
      });

      next.set(id, deleted);

      return next;
    });
  };

  const tableRows = useMemo<DnsRecordData[]>(() => {
    if (!data) return [];

    const existing = data
      .map((configItem, index) => {
        const [name, ttl, type, content] = configItem as DnsPayloadRow;
        const patch = allChangedRecords.get(index);

        // skip deleted
        if (patch?.action === "delete") return null;

        const recordToUse =
          patch?.action === "update"
            ? { ...patch, id: index }
            : dnsPayloadModel({
                id: index,
                name,
                ttl,
                type,
                content,
                action: null,
              });

        return {
          id: recordToUse.id,
          type: recordToUse.type,
          nameFQDN: recordToUse.nameFQDN,
          host: "",
          content: String(recordToUse.content),
          ttl: Number(recordToUse.ttl),
          isModified: patch?.action === "update",
        } as DnsRecordData;
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    const added = Array.from(allChangedRecords.values())
      .filter((r) => r.action === "add")
      .map((r) => ({
        id: r.id,
        type: String(r.type),
        nameFQDN: String(r.nameFQDN),
        host: "",
        content: String(r.content),
        ttl: Number(r.ttl),
        isModified: true,
        isNew: true,
      }));

    return [...existing, ...added];
  }, [data, allChangedRecords]);

  const handleSave = useCallback(() => {
    const recordsToSave = new Map(allChangedRecords);
    updateDnsRecord(recordsToSave);
  }, [allChangedRecords, updateDnsRecord]);

  const handleAddRecord = useCallback(() => setIsAddModalOpen(true), []);
  const handleCloseAddModal = useCallback(() => setIsAddModalOpen(false), []);
  const handleAddRecordSubmit = useCallback(
    (createdRecordMap: Map<number, DnsRecordModel>) => {
      handleEditFormSubmit(createdRecordMap);
      setIsAddModalOpen(false);
    },
    [handleEditFormSubmit],
  );

  const handleFileChange = (uploadedFile: File) => setFile(uploadedFile);
  const handleUpload = () => {
    if (!file) return;

    setImportZoneErrors([]);
    uploadZoneFile(file);
  };

  const { mutate: uploadZoneFile, isPending: isUploadFilePending } =
    useMutation({
      mutationFn: async (file: File) => {
        const result = await apiClient.domains.zoneCreate(Number(domainId), {
          zone_file: file,
        });

        return result.data;
      },
      onSuccess: (response) => {
        enqueueSnackbar("Zone file imported successfully", {
          variant: "success",
        });
        setIsModalOpen(false);
        setFile(null);

        if (!response.zone_import.zone.length || !domain?.name) return;

        const { records, skipped } = buildImportedChanges(
          response.zone_import.zone,
          domain.name,
          domain.integration_type,
        );

        const errors = response.zone_import.errors;
        if (errors && errors.length) {
          setImportZoneErrors(errors);
        }

        if (skipped.length > 0) {
          console.info("Zone import skipped entries:", skipped);
        }

        setData([]);
        setAllChangedRecords(records);
      },
      onError: (error) => {
        console.error("Error importing zone file:", error);
        enqueueSnackbar(
          // @ts-expect-error backend shape
          error.response?.data?.message ||
            "Failed to import zone file. Please check the file format.",
          { variant: "error", persist: true },
        );
      },
    });

  const changesSummary = useMemo<ChangesSummary>(() => {
    const arr = Array.from(allChangedRecords.values());
    const adds = arr.filter(isAdd).length;
    const updates = arr.filter(isUpdate).length;
    const deletes = arr.filter(isDelete).length;

    return { adds, updates, deletes };
  }, [allChangedRecords]);

  const changes: false | string[] = useMemo(() => {
    if (allChangedRecords.size === 0) return false;

    return (
      [
        ["addition", changesSummary.adds],
        ["update", changesSummary.updates],
        ["deletion", changesSummary.deletes],
      ] as [string, number][]
    )
      .filter(([, c]) => c > 0)
      .map(([label, c]) => `${c} ${pluralize(label, c)}`);
  }, [allChangedRecords, changesSummary]);

  const addModalInitialData = useMemo(() => makeAddInitialData(), []);

  const pending = isPolling || isOptimisticUpdatePending;

  if (!data) return <NoData title="No DNS records found." />;

  const isPrerenderingHealthy =
    domain && isDomainPrerenderingStatusHealthy(domain);

  return (
    <>
      <div className={styles.addRecordHeader}>
        {domain?.integration_type === "full" && (
          <Button
            variant="primary"
            className={styles.addRecordButton}
            onClick={() => setIsModalOpen(true)}
            disabled={pending}
          >
            <Icon iconName="PlusIcon" />
            <span>Import Zone File</span>
          </Button>
        )}
        <Button
          variant="primary"
          className={styles.addRecordButton}
          onClick={handleAddRecord}
          disabled={pending}
        >
          <Icon iconName="PlusIcon" />
          <span>Add Record</span>
        </Button>
      </div>

      {importZoneErrors.length > 0 && (
        <ImportZoneErrors
          errors={importZoneErrors}
          onClose={() => setImportZoneErrors([])}
        />
      )}

      <RecordsTable
        rows={tableRows}
        domainName={domain?.name}
        renderActions={(row) => (
          <ActionsSiteSpeedDomain
            tableRow={row}
            handleSubmit={handleEditFormSubmit}
            onDelete={handleDelete}
            domain={domain}
            disabled={pending}
            allTableRows={tableRows}
          />
        )}
      />

      {!isWizard ? (
        <div className={styles.stepContentFooter}>
          <Button
            onClick={handleSave}
            disabled={!changes || pending}
            variant="primary"
          >
            <span className={styles.saveButtonIconTitle}>
              {pending && <Spinner size="1rem" />}
              <span>Save</span>
              {changes && ` (${changes.join(", ")})`}
            </span>
          </Button>
        </div>
      ) : (
        <div className={styles.stepContentFooter}>
          <Button variant="outlined" onClick={handleBack} disabled={pending}>
            Back
          </Button>
          <Button
            onClick={handleSave}
            disabled={pending || !isPrerenderingHealthy}
          >
            {pending && <Spinner size="1rem" />}
            Continue
          </Button>
        </div>
      )}

      <EditRecordModal
        isOpen={isAddModalOpen}
        closeModal={handleCloseAddModal}
        handleSubmit={handleAddRecordSubmit}
        initialData={addModalInitialData}
        allTableRows={tableRows}
        domain={domain}
        mode="create"
      />

      <OriginsStatusModal
        isOpen={isOriginsModalOpen}
        closeModal={() => setIsOriginsModalOpen(false)}
        domainId={domainId!}
        onConfirm={handleOriginsConfirm}
        onError={onErrorChange}
      />

      <Modal
        isOpen={isModalOpen}
        closeModal={() => !isUpdateDnsPending && setIsModalOpen(false)}
        modalLabel={<div>Import DNS Zone File</div>}
      >
        {/* @ts-expect-error TS2339 non-existing className */}
        <div className={styles.fileUploaderContainer}>
          <p>Upload a TXT zone file to import DNS records.</p>
          <div className={styles.fileUploader}>
            <FileUploader
              handleChange={handleFileChange}
              name="zoneFile"
              types={["TXT"]}
              maxSize={5}
              label="Drag & drop your zone file here or click to browse"
              disabled={isUpdateDnsPending}
            />
          </div>
          {file && (
            // @ts-expect-error TS2339 non-existing className
            <div className={styles.fileInfo}>
              <p>Selected file: {file.name}</p>
            </div>
          )}
          <div className={styles.modalActions}>
            <Button
              variant="outlined"
              onClick={() => setIsModalOpen(false)}
              disabled={isUpdateDnsPending}
            >
              Cancel
            </Button>
            <ButtonWithSpinner
              withSpinner={isUploadFilePending}
              onClick={handleUpload}
              disabled={!file || isUpdateDnsPending || isUploadFilePending}
            >
              {isUpdateDnsPending ? "Importing..." : "Import Zone File"}
            </ButtonWithSpinner>
          </div>
        </div>
      </Modal>
    </>
  );
};
